import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Plus, X, ChevronRight, Trash2, Truck, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";

export function daysColor(d) {
  if (d === null || d === undefined) return "#9aa7b8";
  if (d < 0) return "#9c1e2b";
  if (d <= 7) return "#b45309";
  if (d <= 20) return "#8a6d15";
  if (d <= 45) return "#2c4a89";
  return "#6b7a90";
}

const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

function PlanControl({ order, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState((order.due_date || "").slice(0, 10));
  const plan = async (planned) => {
    try {
      await api.post("/calendar/plan", { scope: "order", code: order.code, date: planned ? date : undefined, planned });
      toast.success(planned ? (t("delivery_planned_ok") || "Consegna pianificata") : (t("delivery_unplanned") || "Pianificazione rimossa"));
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const confirmDepart = async () => {
    if (!window.confirm(`Confermi la spedizione dell'ordine ${order.code}? Verrà registrato come consegnato.`)) return;
    try {
      await api.post("/calendar/depart", { scope: "order", code: order.code, force: true });
      toast.success("Spedizione confermata");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="inline-block" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`plan-btn-${order.code}`} onClick={() => setOpen(true)}
        className={`btn-industrial inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[3px] text-xs border whitespace-nowrap ${order.delivery_planned ? "border-[#12a150] text-[#0f7a3d] bg-[#eafaf0] font-500" : "hairline t-muted hover:border-[#33559d]"}`}>
        <Truck size={13} /> {order.delivery_planned ? (t("planned_short") || "pianificata") : (t("plan_delivery") || "Pianifica")}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="panel rounded-[4px] p-5 w-full max-w-xs shadow-lg bg-white animate-rise" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-600 t-head inline-flex items-center gap-2"><Truck size={16} className="text-[#33559d]" /> {t("plan_delivery") || "Pianifica"} · {order.code}</h4>
              <button onClick={() => setOpen(false)} className="t-muted hover:t-head"><X size={18} /></button>
            </div>
            <label className="lbl">{t("delivery_date") || "Data consegna"}</label>
            <input data-testid={`plan-date-${order.code}`} type="date" className="field mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
            <button data-testid={`plan-confirm-${order.code}`} onClick={() => plan(true)} className="btn-primary btn-industrial w-full py-2.5 mt-3 rounded-[3px] text-sm">{t("delivery_plan") || "Conferma consegna pianificata"}</button>
            {order.delivery_planned && !order.completed && (
              <button data-testid={`depart-confirm-${order.code}`} onClick={confirmDepart} className="btn-industrial w-full py-2.5 mt-2 rounded-[3px] text-sm bg-[#0f7a3d] text-white inline-flex items-center justify-center gap-2">
                <Check size={15} /> Conferma spedizione (è partito)
              </button>
            )}
            {order.delivery_planned && (
              <button data-testid={`plan-remove-${order.code}`} onClick={() => plan(false)} className="btn-ghost btn-industrial w-full py-1.5 mt-1 rounded-[3px] text-xs text-[#9c1e2b]">{t("delivery_unplan") || "Rimuovi pianificazione"}</button>
            )}
          </div>
        </div>, document.body)}
    </div>
  );
}

const blankMachine = () => ({ _k: Math.random().toString(36).slice(2), product_id: "", product: "", pieces: 1, value: 0, due_date: "", priority: "normale", work_days: 15 });

function NewOrderModal({ onClose, onCreated }) {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [head, setHead] = useState({ code: "", client: "", final_customer: "" });
  const [clientOpts, setClientOpts] = useState([]);
  const [finalOpts, setFinalOpts] = useState([]);
  const [machines, setMachines] = useState([blankMachine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  useEffect(() => { api.get("/orders/next-code").then((r) => setHead((f) => ({ ...f, code: r.data.code }))).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/orders").then((r) => {
      const dedupe = (vals) => {
        const map = new Map();
        (vals || []).forEach((v) => { const s = (v || "").trim(); if (!s) return; const k = s.toLowerCase(); if (!map.has(k)) map.set(k, s); });
        return Array.from(map.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      };
      setClientOpts(dedupe(r.data.map((o) => o.client)));
      setFinalOpts(dedupe(r.data.map((o) => o.final_customer)));
    }).catch(() => {});
  }, []);

  const setH = (k, v) => setHead((f) => ({ ...f, [k]: v }));
  const setM = (idx, patch) => setMachines((ms) => ms.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const selectProduct = (idx, id) => {
    const p = products.find((x) => x.id === id);
    if (p) setM(idx, { product_id: id, product: p.name, work_days: p.work_days, optionals: [] });
    else setM(idx, { product_id: "", optionals: [] });
  };
  const toggleOpt = (idx, opt) => setMachines((ms) => ms.map((m, i) => {
    if (i !== idx) return m;
    const has = (m.optionals || []).some((o) => o.name === opt.name);
    const optionals = has ? (m.optionals || []).filter((o) => o.name !== opt.name)
      : [...(m.optionals || []), { name: opt.name, price: +opt.price || 0, extra_days: +opt.extra_days || 0 }];
    const delta = (has ? -1 : 1) * (+opt.price || 0);
    return { ...m, optionals, value: Math.max(0, (+m.value || 0) + delta) };
  }));
  const addMachine = () => setMachines((ms) => [...ms, blankMachine()]);
  const rmMachine = (idx) => setMachines((ms) => (ms.length > 1 ? ms.filter((_, i) => i !== idx) : ms));

  const submit = async (e) => {
    e.preventDefault();
    const valid = machines.filter((m) => m.product && m.due_date);
    if (!head.code || !head.client || valid.length === 0) { toast.error("Compila N° ordine, cliente e almeno una macchina"); return; }
    setSaving(true);
    try {
      const payload = {
        code: head.code, client: head.client, final_customer: head.final_customer,
        machines: valid.map((m) => ({
          product: m.product, product_id: m.product_id || null, pieces: +m.pieces || 1,
          value: +m.value || 0, due_date: new Date(m.due_date).toISOString(),
          priority: m.priority, work_days: +m.work_days || 15,
          optionals: (m.optionals || []),
        })),
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`${t("order_no")} ${data.code} · ${data.created} ${t("machines").toLowerCase()}`);
      onCreated();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 bg-black/40 overflow-y-auto">
      <div className="panel rounded-[4px] w-full max-w-2xl p-6 animate-rise max-h-[95vh] sm:max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-700 text-xl t-head tracking-tight">{t("new_order")}</h3>
          <button onClick={onClose} data-testid="close-modal" className="t-muted hover:t-head"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className="lbl">{t("code")}</label>
              <input data-testid="wo-code" required className="field mt-1" placeholder="26-160" value={head.code} onChange={(e) => setH("code", e.target.value)} /></div>
            <div><label className="lbl">{t("client")}</label>
              <input data-testid="wo-client" required list="wo-client-list" autoComplete="off" className="field mt-1" value={head.client} onChange={(e) => setH("client", e.target.value)} />
              <datalist id="wo-client-list">{clientOpts.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div><label className="lbl">{t("final_customer")}</label>
              <input className="field mt-1" list="wo-final-list" autoComplete="off" value={head.final_customer} onChange={(e) => setH("final_customer", e.target.value)} />
              <datalist id="wo-final-list">{finalOpts.map((c) => <option key={c} value={c} />)}</datalist></div>
          </div>

          <div className="flex items-center justify-between">
            <div className="lbl">{t("order_machines")}</div>
            <button type="button" data-testid="add-machine-btn" onClick={addMachine} className="btn-ghost btn-industrial inline-flex items-center gap-1 px-2.5 py-1 rounded-[3px] text-xs">
              <Plus size={13} /> {t("add_machine")}
            </button>
          </div>

          <div className="space-y-3">
            {machines.map((m, idx) => (
              <div key={m._k} className="rounded-[4px] p-3" style={{ background: "#f7f9fc", border: "1px solid #dde3ec" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[11px] t-muted">#{idx + 1}</span>
                  {machines.length > 1 && <button type="button" onClick={() => rmMachine(idx)} className="t-faint hover:text-[#9c1e2b]"><Trash2 size={14} /></button>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="sm:col-span-2">
                    <select data-testid={`m-product-${idx}`} className="field" value={m.product_id} onChange={(e) => selectProduct(idx, e.target.value)}>
                      <option value="">— {t("custom_machine")} —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.work_days}gg</option>)}
                    </select>
                  </div>
                  {!m.product_id && (
                    <div className="sm:col-span-2">
                      <textarea data-testid={`m-name-${idx}`} rows={2}
                        className="field resize-y min-h-[46px] leading-snug break-words"
                        placeholder="Componenti impianto" value={m.product}
                        onChange={(e) => setM(idx, { product: e.target.value })}
                        onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }} />
                    </div>
                  )}
                  <div><label className="lbl">{t("pieces")}</label>
                    <input type="number" min="1" className="field mt-1 py-2" value={m.pieces} onChange={(e) => setM(idx, { pieces: e.target.value })} /></div>
                  <div><label className="lbl">{t("value")} (€)</label>
                    <input type="number" className="field mt-1 py-2" value={m.value} onChange={(e) => setM(idx, { value: e.target.value })} /></div>
                  <div><label className="lbl">{t("due")}</label>
                    <input data-testid={`m-due-${idx}`} type="date" className="field mt-1 py-2" value={m.due_date} onChange={(e) => setM(idx, { due_date: e.target.value })} /></div>
                  <div><label className="lbl">{t("priority")}</label>
                    <select className="field mt-1 py-2" value={m.priority} onChange={(e) => setM(idx, { priority: e.target.value })}>
                      <option value="bassa">{t("bassa")}</option><option value="normale">{t("normale")}</option><option value="alta">{t("alta")}</option>
                    </select></div>
                </div>
                {(() => {
                  const p = products.find((x) => x.id === m.product_id);
                  const opts = p?.optionals || [];
                  if (!opts.length) return null;
                  return (
                    <div className="mt-2.5" data-testid={`m-optionals-${idx}`}>
                      <div className="lbl mb-1">{t("commercial_optionals")}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {opts.map((o) => {
                          const active = (m.optionals || []).some((x) => x.name === o.name);
                          return (
                            <button type="button" key={o.name} data-testid={`m-opt-${idx}-${o.name}`}
                              onClick={() => toggleOpt(idx, o)}
                              className={`text-xs px-2.5 py-1.5 rounded-[3px] border transition-colors ${active ? "border-[#33559d] bg-[#eef2fb] text-[#2c4a89] font-500" : "hairline t-muted bg-white hover:border-[#33559d]"}`}>
                              {o.name}{o.price ? ` · ${euro(o.price)}` : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost btn-industrial flex-1 py-2.5 rounded-[3px]">{t("cancel")}</button>
            <button data-testid="wo-submit" disabled={saving} className="btn-primary btn-industrial flex-1 py-2.5 rounded-[3px] disabled:opacity-60">{t("create")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Production() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState("attivi");
  const [clientF, setClientF] = useState("");
  const [finalF, setFinalF] = useState("");
  const [productF, setProductF] = useState("");
  const [modal, setModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(40);

  const load = useCallback(() => api.get("/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const canCreate = ["management", "sales", "admin", "reparto_tecnico", "amministrazione"].includes(user.role);
  const canSeeValue = ["management", "sales", "admin", "amministrazione"].includes(user.role);
  const canPlan = ["management", "amministrazione", "reparto_tecnico", "admin"].includes(user.role);

  const normC = (s) => (s || "").trim().toLowerCase();
  const dedupeCI = (values) => {
    const map = new Map();
    values.forEach((v) => { const s = (v || "").trim(); if (!s) return; const k = s.toLowerCase(); if (!map.has(k)) map.set(k, s); });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  };
  const clients = useMemo(() => dedupeCI(orders.map((o) => o.client)), [orders]);
  const finalCustomers = useMemo(() => dedupeCI(orders.map((o) => o.final_customer)), [orders]);
  // Filtro macchina basato SOLO sull'Anagrafica Macchine (esclude componenti aggiuntivi)
  const productNames = useMemo(() => Array.from(new Set(
    catalog.map((p) => p.name).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [catalog]);

  const filtered = useMemo(() => orders.filter((o) => {
    if (tab === "attivi" && o.completed) return false;
    if (tab === "completati" && !o.completed) return false;
    if (clientF && normC(o.client) !== normC(clientF)) return false;
    if (finalF && normC(o.final_customer) !== normC(finalF)) return false;
    if (productF && !(o.products || []).includes(productF)) return false;
    return true;
  }), [orders, tab, clientF, finalF, productF]);

  useEffect(() => { setVisibleCount(40); }, [tab, clientF, finalF, productF]);
  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const filteredUnitsTotal = useMemo(() => (
    productF ? filtered.reduce((s, o) => s + (o.product_units?.[productF] || 0), 0) : 0
  ), [filtered, productF]);

  const counts = useMemo(() => ({
    attivi: orders.filter((o) => !o.completed).length,
    completati: orders.filter((o) => o.completed).length,
  }), [orders]);

  return (
    <div className="p-5 lg:p-10 max-w-[1600px] mx-auto animate-rise">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display font-700 text-3xl lg:text-4xl t-head tracking-tight">{t("production")}</h1>
          <p className="t-muted mt-1">{orders.length} {t("orders_word")}</p>
        </div>
        {canCreate && (
          <button data-testid="new-order-button" onClick={() => setModal(true)} className="btn-primary btn-industrial flex items-center gap-2 px-4 py-2.5 text-sm rounded-[3px] min-h-[44px]">
            <Plus size={16} /> {t("new_order")}
          </button>
        )}
      </div>

      {/* Tabs + filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex gap-1 panel rounded-[4px] p-1 w-fit">
          {["attivi", "completati"].map((k) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`btn-industrial px-4 py-2 rounded-[3px] text-sm font-500 ${tab === k ? "bg-[#33559d] text-white" : "t-muted hover:t-head"}`}>
              {t(k === "attivi" ? "tab_active" : "tab_completed")} <span className="font-mono text-xs opacity-80">({counts[k]})</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <select data-testid="filter-client" value={clientF} onChange={(e) => setClientF(e.target.value)} className="field w-auto max-w-full">
            <option value="">{t("all_clients")}</option>
            {clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-final" value={finalF} onChange={(e) => setFinalF(e.target.value)} className="field w-auto max-w-full">
            <option value="">{t("all_final_customers")}</option>
            {finalCustomers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-product" value={productF} onChange={(e) => setProductF(e.target.value)} className="field w-auto max-w-full">
            <option value="">{t("all_products")}</option>
            {productNames.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {(clientF || finalF || productF) && (
            <button data-testid="clear-filters" onClick={() => { setClientF(""); setFinalF(""); setProductF(""); }} className="btn-ghost btn-industrial px-3 py-2 rounded-[3px] text-xs">{t("clear")}</button>
          )}
        </div>
      </div>

      {productF && (
        <div data-testid="filtered-units-banner" className="mb-4 flex items-center gap-3 panel rounded-[4px] px-4 py-3 border-l-[3px] border-l-[#33559d] bg-[#f4f7fd]">
          <span className="text-[13px] t-muted">Totale</span>
          <span className="font-mono font-700 text-[#33559d] text-lg">{filteredUnitsTotal}×</span>
          <span className="font-600 t-head truncate">{productF}</span>
          <span className="text-[12px] t-faint ml-auto shrink-0">{filtered.length} {t("orders_word")}</span>
        </div>
      )}

      <div className="panel rounded-[4px] overflow-x-auto hidden md:block">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left lbl border-b hairline bg-[#f7f9fc]">
              <th className="px-4 py-3">{t("order_no")}</th>
              <th className="px-4 py-3">{t("client")}</th>
              <th className="px-4 py-3">{t("machines")}</th>
              {canSeeValue && <th className="px-4 py-3 text-right">{t("total_value")}</th>}
              <th className="px-4 py-3 w-32">{t("progress")}</th>
              <th className="px-4 py-3">{t("due")}</th>
              <th className="px-4 py-3">{t("status")}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (<tr><td colSpan="8" className="px-4 py-10 text-center t-faint">{t("no_orders")}</td></tr>)}
            {visible.map((o) => (
              <tr key={o.code} data-testid={`order-row-${o.code}`} onClick={() => navigate(`/production/order/${encodeURIComponent(o.code)}`)}
                className={`border-b hairline cursor-pointer transition-colors ${o.delivery_planned && !o.completed ? "bg-[#eafaf0] hover:bg-[#dff4e8]" : "hover:bg-[#eef2fb]"}`}>
                <td className="px-4 py-3 font-mono text-[#33559d] font-500">{o.code}</td>
                <td className="px-4 py-3 t-head">{o.client}<div className="text-[11px] t-faint">{o.final_customer}</div></td>
                <td className="px-4 py-3 t-body">
                  {productF ? (
                    <span data-testid={`order-filtered-count-${o.code}`} className="inline-flex items-center gap-1.5 font-mono text-[#33559d] font-700 bg-[#eef2fb] px-2 py-1 rounded-[3px]">
                      <span className="text-[15px]">{(o.product_units?.[productF] || 0)}×</span>
                      <span className="text-[12px] font-600 not-italic">{productF}</span>
                    </span>
                  ) : (
                    <>
                      <span className="font-mono text-[#2c4a89] font-600">{o.machines_count}</span>
                      <span className="t-faint text-[12px] ml-2 truncate inline-block max-w-[280px] align-bottom">{(o.products || []).slice(0, 3).join(", ")}{o.products?.length > 3 ? "…" : ""}</span>
                    </>
                  )}
                </td>
                {canSeeValue && <td className="px-4 py-3 font-mono t-body text-right">{euro(o.total_value)}</td>}
                <td className="px-4 py-3">
                  <div className="w-full h-1.5 bg-[#eef1f5] rounded-full"><div className="h-full bg-[#33559d] rounded-full" style={{ width: `${o.progress}%` }} /></div>
                  <span className="font-mono text-[10px] t-muted">{o.progress}%</span>
                </td>
                <td className="px-4 py-3 font-mono">
                  <div className="t-body">{o.due_date ? new Date(o.due_date).toLocaleDateString("it-IT") : "—"}</div>
                  {o.days_to_due !== null && !o.completed && (
                    <span className="text-[10px] font-600" style={{ color: daysColor(o.days_to_due) }}>
                      {o.days_to_due < 0 ? `${Math.abs(o.days_to_due)}gg fa` : `${o.days_to_due}gg`}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3"><StatusBadge value={o.order_status} /></td>
                <td className="px-4 py-3 t-faint">
                  <div className="flex items-center gap-2 justify-end">
                    {canPlan && <PlanControl order={o} onDone={load} />}
                    <ChevronRight size={16} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > visible.length && (
          <div className="p-3 text-center border-t hairline">
            <button data-testid="orders-show-more" onClick={() => setVisibleCount((n) => n + 40)}
              className="btn-ghost btn-industrial px-4 py-2 rounded-[3px] text-sm">
              {t("show_more") || "Mostra altri"} ({filtered.length - visible.length})
            </button>
          </div>
        )}
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2.5">
        {filtered.length === 0 && <div className="panel rounded-[4px] py-10 text-center t-faint text-sm">{t("no_orders")}</div>}
        {visible.map((o) => (
          <div key={o.code} data-testid={`order-card-${o.code}`} role="button" tabIndex={0} onClick={() => navigate(`/production/order/${encodeURIComponent(o.code)}`)}
            className={`panel rounded-[4px] w-full text-left p-4 btn-industrial cursor-pointer ${o.delivery_planned && !o.completed ? "bg-[#eafaf0] active:bg-[#dff4e8] border-l-[3px] border-l-[#12a150]" : "active:bg-[#eef2fb]"}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="font-mono text-[#33559d] font-600 text-sm">{o.code}</span>
              <StatusBadge value={o.order_status} />
            </div>
            <div className="t-head font-600">{o.client}</div>
            {o.final_customer && <div className="text-[12px] t-faint">{o.final_customer}</div>}
            <div className="text-[13px] t-muted mt-1">
              {productF ? (
                <span data-testid={`order-card-filtered-count-${o.code}`} className="inline-flex items-center gap-1.5 font-mono text-[#33559d] font-700 bg-[#eef2fb] px-2 py-1 rounded-[3px]">
                  <span className="text-[15px]">{(o.product_units?.[productF] || 0)}×</span>
                  <span className="text-[12px] font-600">{productF}</span>
                </span>
              ) : (
                <>
                  <span className="font-mono text-[#2c4a89] font-600">{o.machines_count}</span> {t("machines").toLowerCase()}
                  <span className="t-faint"> · {(o.products || []).slice(0, 2).join(", ")}{o.products?.length > 2 ? "…" : ""}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2.5">
              <div className="flex-1">
                <div className="w-full h-1.5 bg-[#eef1f5] rounded-full"><div className="h-full bg-[#33559d] rounded-full" style={{ width: `${o.progress}%` }} /></div>
              </div>
              <span className="font-mono text-[11px] t-muted">{o.progress}%</span>
              <span className="font-mono text-[12px]">
                {o.due_date ? new Date(o.due_date).toLocaleDateString("it-IT") : "—"}
                {o.days_to_due !== null && !o.completed && (
                  <span className="ml-1 font-600" style={{ color: daysColor(o.days_to_due) }}>
                    {o.days_to_due < 0 ? `${Math.abs(o.days_to_due)}gg fa` : `${o.days_to_due}gg`}
                  </span>
                )}
              </span>
            </div>
            {canSeeValue && <div className="font-mono text-[13px] t-body mt-1.5">{euro(o.total_value)}</div>}
            {canPlan && <div className="mt-2.5 pt-2.5 border-t hairline"><PlanControl order={o} onDone={load} /></div>}
          </div>
        ))}
        {filtered.length > visible.length && (
          <button data-testid="orders-show-more-mobile" onClick={() => setVisibleCount((n) => n + 40)}
            className="btn-ghost btn-industrial w-full py-2.5 rounded-[3px] text-sm">
            {t("show_more") || "Mostra altri"} ({filtered.length - visible.length})
          </button>
        )}
      </div>

      {modal && <NewOrderModal onClose={() => setModal(false)} onCreated={() => load()} />}
    </div>
  );
}
